import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { setActionSpinner, setFetchSpinner, setPagination } from "Store/global/actions";
import * as instructorActions from "Store/instructors/actions";
import { setInstructorSummary } from "Store/instructors/actions";
import * as instructorTypes from "Store/instructors/constants";
import { makeSelectActiveLocation } from "Store/locations/selectors";
import { makeSelectCurrentInstructor } from "./selectors";

import ERRORS from "constants/localization/errors";
import SUCCESSES from "constants/localization/successes";
import { convertMetaToPagination, renderApiParameters } from "Store/global/saga";
import * as globalSelectors from "Store/global/selectors";
import * as instructorApi from "Store/instructors/api";
import { fetchStudentsApi } from "Store/students/api";
import {isNotEmpty, removeDuplicateFromArrayObjects} from "utils/objectsHelper";
import { Toast } from "utils/toast";

function* handleFetchInstructorSummary(action) {
  try {
    const { error, data } = yield instructorApi.fetchInstructorSummaryApi(action.instructorId);
    if (error) {
      Toast.error(ERRORS.handleFetchInstructorSummary, error);
    }
    yield put(instructorActions.setInstructorSummary(data.summary));
  } catch (error) {
    yield put(setInstructorSummary(null));
    Toast.error(ERRORS.handleFetchInstructorSummary, error);
  }
}

function* handleFetchInstructorsSaga() {
  yield put(setFetchSpinner(true));
  try {
    const parameters = yield renderApiParameters(globalSelectors, "instructors");
    const { error, data, meta } = yield instructorApi.fetchInstructorsApi(parameters);
    if (error) {
      Toast.error(ERRORS.handleFetchInstructors, error);
    } else {
      yield put(setPagination(convertMetaToPagination(meta), "instructors"));
      yield put(instructorActions.setInstructors(data));
      yield put(setFetchSpinner(false));
      for (const instructor of data as Instructor[]) {
        yield call(handleFetchInstructorStatsSaga, {
          payload: instructor.id,
          type: "HANDLE_FETCH_INSTRUCTOR_STATS"
        });
      }
    }
  } catch (error) {
    yield put(instructorActions.setInstructors(null));
    Toast.error(ERRORS.handleFetchInstructors, error);
  } finally {
    yield put(setFetchSpinner(false));
  }
}

function* handleFetchInstructorStatsSaga(action: instructorTypes.IHandleFetchInstructorStats) {
  try {
    const instructorId = action.payload;
    const { data } = yield instructorApi.fetchInstructorStatsApi(instructorId);
    yield put(instructorActions.setInstructorStats(data, instructorId));
  } catch (error) {}
}

function* handleArchiveInstructorSaga(action: instructorTypes.IHandleArchiveInstructor) {
  yield put(setActionSpinner(true));
  try {
    const { payload, source } = action;
    const { error } = yield instructorApi.archiveInstructorApi(payload);
    if (error) {
      Toast.error(ERRORS.handleUpdateInstructor, error);
    } else {
      Toast.success(SUCCESSES.handleArchiveInstructorSaga(payload.archived));
      if (source === "LIST") {
        yield call(handleFetchInstructorsSaga);
      } else {
        yield call(handleFetchInstructorSaga, {
          instructorId: payload.user_id,
          type: "HANDLE_FETCH_INSTRUCTOR"
        });
      }
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateInstructor, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}
function* handleDeactivateInstructorSaga(action: instructorTypes.IHandleDeactivateInstructor) {
  yield put(setActionSpinner(true));
  try {
    const { payload, source } = action;
    const { error } = yield instructorApi.deactivateInstructorApi(payload);
    if (error) {
      Toast.error(ERRORS.handleDeactivateInstructor, error);
    } else {
      Toast.success(SUCCESSES.handleDeactivateInstructor);
      if (source === "LIST") {
        if (source === "LIST") {
          yield call(handleFetchInstructorsSaga);
        } else {
          yield call(handleFetchInstructorSaga, {
            instructorId: payload.instructor_id,
            type: "HANDLE_FETCH_INSTRUCTOR"
          });
        }
      }
    }
  } catch (error) {
    Toast.error(ERRORS.handleDeactivateInstructor, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleFetchInstructorSaga(action: instructorTypes.IHandleFetchInstructor) {
  yield put(setFetchSpinner(true));
  const { instructorId } = action;
  try {
    const { data, error } = yield instructorApi.fetchInstructorApi(instructorId);
    if (error) {
      Toast.error(ERRORS.handleFetchInstructor, error);
    } else {
      yield put(instructorActions.setInstructor(data));
      yield put(instructorActions.fetchInstructorSummary(instructorId));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchInstructor, error);
  } finally {
    yield put(setFetchSpinner(false));
  }
}

function* handleCreateInstructorSaga(action: instructorTypes.IHandleCreateInstructor) {
  yield put(setActionSpinner(true));
  try {
    const { payload } = action;
    const { error } = yield instructorApi.createNewInstructorApi(payload);
    if (error) {
      const keyErrors = !!isNotEmpty(error) && Object.keys(error);

      if (keyErrors.length) {
        keyErrors.map((keyError) => Toast.error(ERRORS.handleCreateInstructor, error[keyError].join(", ")));
      }
    } else {
      Toast.success(SUCCESSES.handleCreateInstructor);
      call(handleFetchInstructorsSaga);
    }
  } catch (error) {
    const keyErrors = !!isNotEmpty(error) && Object.keys(error);

    if (keyErrors.length) {
      keyErrors.map((keyError) => Toast.error(ERRORS.handleCreateInstructor, error[keyError].join(", ")));
    }
  } finally {
    yield put(setActionSpinner(false));
  }
}

function* handleFetchInstructorStudentsSaga() {
  yield put(setFetchSpinner(true));
  try {
    const parameters = yield renderApiParameters(globalSelectors, "instructorStudents");
    const currentInstructor = yield select(makeSelectCurrentInstructor);
    const { data, error, meta } = yield fetchStudentsApi(
      parameters + `&instructor=${currentInstructor.id}`
    );

    if (error) {
      Toast.error(ERRORS.handleFetchInstructorStudents, error);
    } else {
      yield put(setPagination(convertMetaToPagination(meta), "instructorStudents"));
      yield put(instructorActions.setInstructorStudents(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchInstructorStudents, error);
  } finally {
    yield put(setFetchSpinner(false));
  }
}

function* handleUpdateInstructorSaga(action: instructorTypes.IHandleUpdateInstructor) {
  try {
    yield put(setActionSpinner(true));
    const currentInstructor: Instructor = yield select(makeSelectCurrentInstructor);
    const currentLocation: TLocation = yield select(makeSelectActiveLocation);
    const { instructorAddress, instructorInfo, locations, locationsManage, fullAccess } =
      action.payload;
    const currentFullAccess = currentInstructor?.current_location_role === "ADMIN";
    const instructorLocationsIds = currentInstructor?.locations.map((location) => location?.id);
    const locationsToRemove = locations?.removedLocations.filter((location) =>
      instructorLocationsIds.includes(location.id)
    );
    const fullAccessPayload: FullAccessToOrganizationRequest = {
      instructor_id: currentInstructor?.id,
      organization_id: currentLocation?.organization.id
    };

    yield call(handleUpdateInstructorAddressSaga, {
      payload: instructorAddress,
      type: "HANDLE_UPDATE_INSTRUCTOR_ADDRESS"
    });

    yield call(handleUpdateInstructorInfoSaga, {
      payload: instructorInfo,
      type: "HANDLE_UPDATE_INSTRUCTOR_INFO"
    });

    // LOCATIONS
    if (locations?.newLocations.length) {
      const locationsToSave = removeDuplicateFromArrayObjects(
        locations.newLocations,
        currentInstructor.locations
      );
      const payload = {
        instructor_id: currentInstructor.id,
        locations: locationsToSave,
        role: "INSTRUCTOR"
      };
      yield call(handleAddInstructorToLocationSaga, {
        payload,
        type: "HANDLE_ADD_INSTRUCTOR_LOCATION"
      });
    }

    if (locationsToRemove?.length) {
      const payload = {
        instructor_id: currentInstructor.id,
        locations: locationsToRemove
      };
      yield call(handleRemoveInstructorFromLocationSaga, {
        payload: payload,
        type: "HANDLE_REMOVE_INSTRUCTOR_LOCATION"
      });
    }
    //LOCATIONS_MANAGE
    if (locationsManage !== undefined) {
      const locationsToSave = removeDuplicateFromArrayObjects(
        locationsManage.newLocations,
        currentInstructor.locations.filter((location) => location.role === "INSTRUCTOR_MANAGER")
      );
      const payload: AddInstructorLocation = {
        instructor_id: currentInstructor.id,
        locations: locationsToSave,
        role: "INSTRUCTOR_MANAGER"
      };
      if (locationsManage?.newLocations.length) {
        yield call(handleAddInstructorToLocationSaga, {
          payload,
          type: "HANDLE_ADD_INSTRUCTOR_LOCATION"
        });
      }
      if (locationsManage?.removedLocations.length) {
        yield call(handleAddInstructorToLocationSaga, {
          payload: {
            ...payload,
            role: "INSTRUCTOR",
            locations: locationsManage.removedLocations
          },
          type: "HANDLE_ADD_INSTRUCTOR_LOCATION"
        });
      }
    }

    //FULL_ACCESS
    if (fullAccess !== undefined) {
      if (currentFullAccess !== fullAccess) {
        if (fullAccess) {
          yield call(handleGrantFullAccessSaga, {
            payload: fullAccessPayload,
            type: "HANDLE_GRANT_FULL_ACCESS"
          });
        } else {
          yield call(handleRevokeFullAccessSaga, {
            payload: fullAccessPayload,
            type: "HANDLE_REVOKE_FULL_ACCESS"
          });
        }
      }
    }

    yield call(handleFetchInstructorSaga, {
      instructorId: currentInstructor.id,
      type: "HANDLE_FETCH_INSTRUCTOR"
    });
  } catch (error) {
    Toast.error(ERRORS.handleUpdateInstructor, error);
  } finally {
    yield put(setActionSpinner(false));
  }
}
function* handleUpdateInstructorAddressSaga(
  action: instructorTypes.IHandleUpdateInstructorAddress
) {
  try {
    const { payload } = action;
    const { error } = yield instructorApi.updateInstructorAddressApi(payload);
    if (error) {
      Toast.error(ERRORS.handleUpdateInstructorAddress, error);
    } else {
      Toast.success(SUCCESSES.handleUpdateInstructorAddress);
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateInstructorAddress, error);
  }
}

function* handleUpdateInstructorInfoSaga(action: instructorTypes.IHandleUpdateInstructorInfo) {
  try {
    const { payload } = action;
    const { error } = yield instructorApi.updateInstructorApi(payload);
    if (error) {
      Toast.error(ERRORS.handleUpdateInstructor, error);
    } else {
      Toast.success(SUCCESSES.handleUpdateInstructor);
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateInstructor, error);
  }
}

function* handleAddInstructorToLocationSaga(action: instructorTypes.IHandleAddInstructorLocation) {
  try {
    const { instructor_id, locations, role } = action.payload;
    for (const location of locations) {
      const { error } = yield instructorApi.addInstructorToLocationApi({
        instructor_id,
        location_id: location.id,
        role: role
      });
    }
  } catch (error) {
    Toast.error(ERRORS.addInstructorToLocation);
  }
}

function* handleRemoveInstructorFromLocationSaga(
  action: instructorTypes.IHandleRemoveInstructorLocation
) {
  try {
    const { instructor_id, locations } = action.payload;
    for (const location of locations) {
      const { error } = yield instructorApi.removeInstructorFromLocationApi({
        instructor_id,
        location_id: location.id
      });
    }
  } catch (error) {
    Toast.error(ERRORS.removeInstructorFromLocation);
  }
}

function* handleFetchInstructorClassroomSaga(
  action: instructorTypes.IHandleFetchInstructorClassrooms
) {
  try {
    const { error, data } = yield instructorApi.fetchInstructorClassroomsApi(action.payload);
    if (error) {
    } else {
      yield put(instructorActions.setInstructorClassrooms(data));
    }
  } catch (error) {
    Toast.error(ERRORS.fetchClassroom);
  }
}

function* handleGrantFullAccessSaga(action: instructorTypes.IGrantFullAccess) {
  try {
    const { error } = yield instructorApi.grantInstructorFullAccessToOrganizationApi(
      action.payload
    );
    if (error) {
      return Toast.error(ERRORS.grantInstructorFullAccessToOrganization);
    }
  } catch (error) {
    Toast.error(ERRORS.grantInstructorFullAccessToOrganization);
  }
}

function* handleRevokeFullAccessSaga(action: instructorTypes.IRevokeEFullAccess) {
  try {
    const { error } = yield instructorApi.revokeInstructorFullAccessToOrganizationApi(
      action.payload
    );
    if (error) {
      Toast.error(ERRORS.revokeInstructorFullAccessToOrganization);
    }
    Toast.success(SUCCESSES.handleRevokeFullAccessSaga);
  } catch (error) {
    Toast.error(ERRORS.revokeInstructorFullAccessToOrganization);
  }
}

export default function* instructorSaga() {
  yield takeLatest(instructorTypes.HANDLE_FETCH_INSTRUCTORS, handleFetchInstructorsSaga);
  yield takeLatest(instructorTypes.HANDLE_FETCH_INSTRUCTOR, handleFetchInstructorSaga);
  yield takeLatest(instructorTypes.HANDLE_FETCH_INSTRUCTOR_SUMMARY, handleFetchInstructorSummary);

  yield takeLatest(instructorTypes.HANDLE_CREATE_INSTRUCTOR, handleCreateInstructorSaga);
  yield takeLatest(instructorTypes.HANDLE_DEACTIVATE_INSTRUCTOR, handleDeactivateInstructorSaga);
  yield takeLatest(instructorTypes.HANDLE_UPDATE_INSTRUCTOR_INFO, handleUpdateInstructorInfoSaga);
  yield takeLatest(
    instructorTypes.HANDLE_UPDATE_INSTRUCTOR_ADDRESS,
    handleUpdateInstructorAddressSaga
  );
  yield takeLatest(
    instructorTypes.HANDLE_ADD_INSTRUCTOR_LOCATION,
    handleAddInstructorToLocationSaga
  );
  yield takeEvery(
    instructorTypes.HANDLE_REMOVE_INSTRUCTOR_LOCATION,
    handleRemoveInstructorFromLocationSaga
  );
  yield takeLatest(
    instructorTypes.HANDLE_FETCH_INSTRUCTOR_STUDENTS,
    handleFetchInstructorStudentsSaga
  );
  yield takeLatest(instructorTypes.HANDLE_ARCHIVE_INSTRUCTOR, handleArchiveInstructorSaga);
  yield takeLatest(instructorTypes.HANDLE_UPDATE_INSTRUCTOR, handleUpdateInstructorSaga);
  yield takeLatest(
    instructorTypes.HANDLE_FETCH_INSTRUCTOR_CLASSROOMS,
    handleFetchInstructorClassroomSaga
  );
  yield takeLatest(instructorTypes.HANDLE_FETCH_INSTRUCTOR_STATS, handleFetchInstructorStatsSaga);
  yield takeLatest(instructorTypes.HANDLE_GRANT_FULL_ACCESS, handleGrantFullAccessSaga);
  yield takeLatest(instructorTypes.HANDLE_REVOKE_FULL_ACCESS, handleRevokeFullAccessSaga);
}
